import { useDiningAreas } from '@/features/pos/hooks/useDiningAreas';
import { useRestaurantTables } from '@/features/pos/hooks/useRestaurantTables';
import { useActiveOrders } from '@/features/pos/hooks/usePOSOrders';
import { Card } from '@/components/ui/card';
import { MapPin, Clock, Users, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { TableOrderDialog } from './TableOrderDialog';

interface TableFloorViewProps {
  onNewOrder?: (areaId: string, tableId: string, tableNumber: string) => void;
  onSwitchToProducts?: () => void;
}

export const TableFloorView = ({ onNewOrder, onSwitchToProducts }: TableFloorViewProps) => {
  const { data: areas = [], isLoading: areasLoading } = useDiningAreas();
  const { data: allTables = [], isLoading: tablesLoading } = useRestaurantTables();
  const { data: activeOrders = [], isLoading: ordersLoading } = useActiveOrders();
  const [selectedTable, setSelectedTable] = useState<{ 
    areaId: string; 
    tableId: string;
    tableNumber: string;
  } | null>(null);

  const handleTableClick = (areaId: string, tableId: string, tableNumber: string) => {
    setSelectedTable({ areaId, tableId, tableNumber });
  };

  if (areasLoading || tablesLoading || ordersLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (areas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <MapPin className="w-16 h-16 mb-4 opacity-20" />
        <p className="text-lg font-medium">Aucune zone configurée</p>
        <p className="text-sm">Configurez vos zones dans l'admin POS</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 overflow-auto">
      {areas.map((area) => {
        const areaTables = allTables.filter(t => t.area_id === area.id);
        const occupiedCount = areaTables.filter(t => t.status === 'occupied').length;

        return (
          <div key={area.id}>
            {/* Area Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: area.color + '20' }}
                >
                  <MapPin className="w-6 h-6" style={{ color: area.color }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{area.name}</h2>
                  {area.name_ar && (
                    <p className="text-sm text-gray-600">{area.name_ar}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-600">
                  <span className="font-medium text-gray-900">{occupiedCount}</span> / {areaTables.length} occupées
                </span>
                <span className="capitalize px-3 py-1 rounded-full bg-gray-100 text-gray-700">
                  {area.area_type}
                </span>
              </div>
            </div>

            {/* Tables Grid */}
            {areaTables.length === 0 ? (
              <Card className="p-8 text-center text-gray-400">
                <p>Aucune table dans cette zone</p>
                <p className="text-sm mt-1">Ajoutez des tables dans l'admin</p>
              </Card>
            ) : (
              <div className="grid grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {areaTables.map((table) => {
                  // Find order for this table
                  const tableOrder = activeOrders.find(o => o.table_id === table.id);
                  
                  return (
                    <TableCard
                      key={table.id}
                      table={table}
                      order={tableOrder}
                      onClick={() => handleTableClick(area.id, table.id, table.table_number)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Table Order Dialog */}
      {selectedTable && (
        <TableOrderDialog
          open={!!selectedTable}
          onClose={() => setSelectedTable(null)}
          areaId={selectedTable.areaId}
          tableId={selectedTable.tableId}
          tableNumber={selectedTable.tableNumber}
          onSwitchToProducts={onSwitchToProducts}
        />
      )}
    </div>
  );
};

interface TableCardProps {
  table: any;
  order?: any;
  onClick: () => void;
}

const TableCard = ({ table, order, onClick }: TableCardProps) => {
  const [showDetails, setShowDetails] = useState(false);
  
  // Calculate duration if order exists
  const duration = order ? Math.floor((new Date().getTime() - new Date(order.started_at).getTime()) / 60000) : 0;

  return (
    <Card
      className={cn(
        'relative p-4 cursor-pointer transition-all hover:shadow-lg group',
        table.status === 'occupied' || order
          ? 'bg-red-50 border-red-300 hover:border-red-400'
          : table.status === 'reserved'
          ? 'bg-yellow-50 border-yellow-300 hover:border-yellow-400'
          : 'bg-emerald-50 border-emerald-300 hover:border-emerald-400'
      )}
      onClick={onClick}
      onMouseEnter={() => setShowDetails(true)}
      onMouseLeave={() => setShowDetails(false)}
    >
      {/* Status Badge */}
      <div
        className={cn(
          'absolute top-2 right-2 w-3 h-3 rounded-full',
          order?.status === 'ready' ? 'bg-green-500 animate-pulse' : 
          table.status === 'occupied' || order ? 'bg-red-500' : 
          table.status === 'reserved' ? 'bg-yellow-500' : 
          'bg-emerald-500'
        )}
      />

      {/* Ready Alert Banner */}
      {order?.status === 'ready' && (
        <div className="absolute top-0 left-0 right-0 bg-green-500 text-white text-xs font-bold py-1 px-2 text-center animate-pulse">
          ✅ PRÊTE!
        </div>
      )}

      {/* Table Info */}
      <div className="text-center">
        <div className="mb-2">
          <p className="text-2xl font-bold text-gray-900">{table.table_number}</p>
          {table.table_name && (
            <p className="text-xs text-gray-600">{table.table_name}</p>
          )}
        </div>

        {/* Status Badges */}
        {order && (
          <div className="flex items-center justify-center gap-1 mb-2">
            {/* Placed */}
            <div className={cn(
              "w-2 h-2 rounded-full",
              order.status ? "bg-blue-500" : "bg-gray-300"
            )} />
            
            {/* Accepted/Confirmed */}
            <div className={cn(
              "w-2 h-2 rounded-full",
              ['confirmed', 'preparing', 'ready'].includes(order.status) ? "bg-purple-500" : "bg-gray-300"
            )} />
            
            {/* Preparing */}
            <div className={cn(
              "w-2 h-2 rounded-full",
              ['preparing', 'ready'].includes(order.status) ? "bg-orange-500" : "bg-gray-300"
            )} />
            
            {/* Ready */}
            <div className={cn(
              "w-2 h-2 rounded-full",
              order.status === 'ready' ? "bg-green-500 animate-pulse" : "bg-gray-300"
            )} />
          </div>
        )}

        {order && (
          <>
            <div className="flex items-center justify-center gap-1 text-sm font-semibold text-red-700 mb-1">
              <span>{order.total_amount.toFixed(2)} DA</span>
            </div>
            <div className="flex items-center justify-center gap-1 text-xs text-gray-600">
              <Clock className="w-3 h-3" />
              <span>{duration} min</span>
            </div>
          </>
        )}

        <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mt-2">
          <Users className="w-3 h-3" />
          <span>{table.seats} places</span>
        </div>

        <p className="text-xs font-medium mt-2 capitalize text-gray-700">
          {order ? 'Occupée' :
           table.status === 'reserved' ? 'Réservée' :
           'Disponible'}
        </p>
      </div>

      {/* Hover Details - Order Items */}
      {showDetails && order && order.items && order.items.length > 0 && (
        <div className="absolute left-0 top-full mt-2 w-64 bg-card border border-border rounded-lg shadow-xl p-3 z-10">
          <p className="text-xs font-semibold text-gray-700 mb-2">Commande en cours:</p>
          <div className="space-y-1">
            {order.items.map((item: any) => (
              <div key={item.id}>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">
                    {item.quantity}x {item.product_name}
                  </span>
                  <span className="font-medium text-gray-900">
                    {item.total_amount.toFixed(2)} DA
                  </span>
                </div>
                {item.notes && (
                  <p className="text-xs text-orange-600 italic ml-4">📝 {item.notes}</p>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200 mt-2 pt-2">
            <div className="flex justify-between text-sm font-bold">
              <span>Total:</span>
              <span className="text-red-600">{order.total_amount.toFixed(2)} DA</span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
